import type { i18n as I18nInstance } from 'i18next';
import type {
  BasketTableAnswerItem,
  EducationContent,
  QuestionConfigBranchLoaderProps,
  QuestionConfigQuestionType,
} from '/index';

import { basketTableTotalTypes, questionScopeCategories, questionSelectorOptions } from '/business/shared/util/Statics';
import formatNumber from '/shared/functions/formatNumber';

import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import WasteIcon from '/business/shared/images/icons/scope3-waste.svg';
import initiativeCardBackground from '/business/shared/images/initiatives/Waste-card-1x.jpg';
import initiativeBackground from '/business/shared/images/initiatives/Waste-card-2x.jpg';

import useFormatSharedCustomScope1 from '/business/shared/components/pages/question/formats/customDataFormats/useFormatSharedCustomScope1';
import useFormatSharedSupplierScope1Ver2 from '/business/shared/components/pages/question/formats/supplierDataFormats/useFormatSharedSupplierScope1Ver2';
import { bottomContentsSupplier } from '/business/shared/components/questions/scope3/_education/bottomScope3';
import educationContentJson from '/business/shared/components/questions/scope3/_education/wasteGeneratedInOps.json';
import useGeneratedWasteOpsSchema from '/business/shared/components/questions/scope3/_hooks/useGeneratedWasteOpsSchema';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

type ConfigureWasteGeneratedInOpsOptions = {
  hasScopeBasedOrganisation?: boolean;
  i18n: I18nInstance;
  reductionCardsSlides?: string[];
};

type WasteGeneratedTableData = {
  quantity?: number | string | null;
  tonnage?: number | null;
  wasteUnit?: string | null;
};

function isEducationContent(value: object): value is EducationContent {
  return (
    typeof value === 'object' &&
    value !== null &&
    'topParas' in value &&
    Array.isArray(value.topParas) &&
    'exampleParas' in value &&
    Array.isArray(value.exampleParas)
  );
}

function getEducationContent(): EducationContent {
  if (!isEducationContent(educationContentJson)) {
    throw new Error('Waste generated in operations education content is not configured');
  }
  return educationContentJson;
}

function formatWasteQuantity({ i18n, quantity, wasteUnit }: { i18n: I18nInstance } & WasteGeneratedTableData) {
  const unit = wasteUnit ?? 'units';
  return `${quantity} ${i18n.t('business:emissionFactors.volumes.'.concat(unit), unit)}`;
}

export const getConfigureWasteGeneratedInOps = ({
  i18n,
  hasScopeBasedOrganisation = true,
  reductionCardsSlides = [], // this prop is reductionv2 ONLY, this question was developed after that
}: ConfigureWasteGeneratedInOpsOptions): QuestionConfigQuestionType => {
  return {
    id: 'waste-generated',
    scopeCategory: hasScopeBasedOrganisation
      ? questionScopeCategories.SCOPE_3
      : questionScopeCategories.BUILDING_EMISSIONS,
    isCompletePropName: 'isWasteGeneratedInOpsComplete',
    apiKey: 'calculation-wasteops',
    title: i18n.t('business:Questions.calculations.wasteOps.title'),

    initiative: {
      formatForInitiativeSummary: () => {
        return {
          isShowingReportingPeriod: false,
          isShowingReportingPeriodNoReduction: false,
          category: false,
          description: false,
          descriptionNoReduction: false,
        };
      },
      getVisuals: ({ isBpOffering }) => {
        if (!isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
        if (isBpOffering) {
          return {
            description: false,
            background: initiativeBackground,
            useMask: false,
            cardBackground: initiativeCardBackground,
          };
        }
      },
    },
    primaryI18nKey: 'business:QuestionPage.forms.emissionSelector.wasteGeneratedInOps',
    subTextI18nKey: 'business:QuestionPage.forms.emissionSelector.wasteGeneratedInOpsTip',
    overviewPageTitleI18nKey: 'business:QuestionPage.forms.emissionSelector.wasteGeneratedInOps',
    reductionPageKey: 'wastegeneratedinops',
    reductionPageCards: reductionCardsSlides,
    icon: WasteIcon,
    useHookLoader: useGeneratedWasteOpsSchema,
    table: {
      title: i18n.t('business:Questions.calculations.wasteOps.table.title'),
      calculateTotal: (allItems: BasketTableAnswerItem[]) => allItems.length,
      totalType: basketTableTotalTypes.COUNT,
      headings: [
        i18n.t('business:Questions.calculations.wasteOps.table.heading1'),
        i18n.t('business:Questions.calculations.wasteOps.table.heading2'),
        i18n.t('business:Questions.calculations.wasteOps.table.heading3'),
        i18n.t('business:Questions.calculations.wasteOps.table.heading4'),
      ],
      cells: [
        'wasteType',
        ({ quantity, wasteUnit }: WasteGeneratedTableData) => formatWasteQuantity({ i18n, quantity, wasteUnit }),
        'treatmentType',
        ({ tonnage }: WasteGeneratedTableData) => `${formatNumber(tonnage)} tCO\u2082e`,
      ],
      supplier: {
        columns: [
          'emissionSource',
          'supplierName',
          'supplierIntensityDataOrAbsoluteEmissions',
          'quantity',
          'carbonEmissions',
        ],
      },
      custom: {
        columns: ['emissionSource', 'intensityValue', 'unitOfMeasurement', 'quantity', 'carbonEmissions'],
      },
    },
    editPageTitle: i18n.t('business:Questions.calculations.wasteOps.editTitle'),

    details: {
      title: 'wasteType',
      items: [
        'wasteType',
        ({ quantity, wasteUnit }: WasteGeneratedTableData) => formatWasteQuantity({ i18n, quantity, wasteUnit }),
        ({ tonnage }: WasteGeneratedTableData) => `${formatNumber(tonnage)} ${i18n.t('business:Snippets.tonnes')}`,
        {
          value: 'dataSource',
          title: i18n.t('business:Questions.calculations.wasteOps.details.title'),
        },
      ],
    },
    selector: {
      injectQuestionBranchSelector: true,
      questionBranchEnableCustomData: true,
      questionBranchEnableSupplierData: true,
      para: 'To calculate emissions associated with the treatment and disposal of waste generate in operations by your business, you can use a combination of activity data, supplier data, and custom data.',
      options: [
        {
          title: questionSelectorOptions.ACTIVITY,
          requiredData: 'Type of waste, quantity disposed of or treated.',
        },
        {
          title: questionSelectorOptions.SUPPLIER,
          requiredData:
            'Type of waste, supplier name and intensity data/absolute emissions, quantity disposed of or treated.',
        },
        {
          title: questionSelectorOptions.CUSTOM,
          requiredData: 'Emission source, intensity data/absolute emissions, quantity disposed of or treated.',
        },
      ],
    },
    education: {
      hasEducationWidget: true,
      educationContent: getEducationContent(),
      bottomContentsSupplier: bottomContentsSupplier,
    },
    useQuestionBranchSupplierLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedSupplierScope1Ver2({
        ...props,
        questionFormatName: 'waste-generated',
        emissionSourcePlaceholder: 'Select type of waste',
        sourceErrMsg: 'Please select type of waste',
      }),
    useQuestionBranchCustomLoader: (props: QuestionConfigBranchLoaderProps) =>
      useFormatSharedCustomScope1({ ...props, questionFormatName: 'waste-generated' }),
    supplierAndCustomSubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          Enter the type of waste was generated in operations by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
    activitySubheader: (
      <>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          What type of waste was generated in operations by your business during the reporting period:{' '}
          <ReportingPeriod $green />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>For more information, refer to the "Help" section for detailed guidance.</div>
        </StyledInfoFlash>
      </>
    ),
  };
};
